// Projects page with filtering functionality
import React, { useState, useMemo } from "react";
import styled from "styled-components";
import ProjectCard from "./ProjectCard";
import ProjectData from "./projectData";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../translations/translations";
import {
  ReactIcon,
  VueIcon,
  RubyIcon,
  RailsIcon,
  TypescriptIcon,
  JavascriptIcon,
  SupabaseIcon,
  FirebaseIcon,
  ReduxIcon,
  TailwindIcon,
  ZustandIcon,
  VuetifyIcon,
  QuasarIcon,
  PiniaIcon,
  VueXIcon,
} from "@/assets/icons";

// Helper function to check if project uses a specific stack
const projectUsesStack = (project, stackFilter) => {
  if (!stackFilter) return true;

  const stackLower = stackFilter.toLowerCase();
  const projectStack = project.stack?.toLowerCase() || "";

  // Check stack field
  if (projectStack.includes(stackLower)) return true;

  // Check icons array
  const iconNames = {
    react: ReactIcon,
    vue: VueIcon,
    ruby: RubyIcon,
    rails: RailsIcon,
    typescript: TypescriptIcon,
    javascript: JavascriptIcon,
    redux: ReduxIcon,
    tailwind: TailwindIcon,
    firebase: FirebaseIcon,
    supabase: SupabaseIcon,
    zustand: ZustandIcon,
    vuetify: VuetifyIcon,
    quasar: QuasarIcon,
    pinia: PiniaIcon,
    vuex: VueXIcon,
  };

  const targetIcon = iconNames[stackLower];
  if (!targetIcon) return false;

  return project.icons?.some((icon) => icon === targetIcon) || false;
};

// Helper function to determine project category
const getProjectCategory = (project) => {
  const icons = project.icons || [];
  const hasRuby = icons.some((icon) => icon === RubyIcon);
  const hasReact = icons.some((icon) => icon === ReactIcon);
  const hasVue = icons.some((icon) => icon === VueIcon);
  const hasSupabase = icons.some((icon) => icon === SupabaseIcon);
  const hasFirebase = icons.some((icon) => icon === FirebaseIcon);

  // Check for backend services (Rails, Ruby, Supabase, Firebase)
  const hasRails = icons.some((icon) => icon === RailsIcon);
  const hasBackendService = hasRails || hasRuby || hasSupabase || hasFirebase;

  // Check for frontend frameworks
  const hasFrontendFramework = hasReact || hasVue;

  // Determine category
  if (hasBackendService && hasFrontendFramework) {
    return "fullstack";
  } else if (hasBackendService) {
    return "backend";
  } else if (hasFrontendFramework) {
    return "frontend";
  }

  // Default based on stack
  if (project.stack === "Ruby") return "backend";
  if (project.stack === "React" || project.stack === "Vue") return "frontend";

  return "frontend"; // Default
};

function Projects() {
  const { language } = useLanguage();
  const [selectedStack, setSelectedStack] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Filter projects based on selected filters
  const filteredProjects = useMemo(() => {
    let filtered = ProjectData;

    // Filter by stack
    if (selectedStack) {
      filtered = filtered.filter((project) =>
        projectUsesStack(project, selectedStack)
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((project) => {
        const category = getProjectCategory(project);
        // Fullstack projects should be included in both frontend and backend filters
        if (selectedCategory === "frontend") {
          return category === "frontend" || category === "fullstack";
        }
        if (selectedCategory === "backend") {
          return category === "backend" || category === "fullstack";
        }
        return category === selectedCategory;
      });
    }

    return filtered;
  }, [selectedStack, selectedCategory]);

  // Separate filtered projects by type
  const professionalProjects = filteredProjects.filter(
    (project) => project.type === "pro"
  );
  const otherProjects = filteredProjects.filter(
    (project) => project.type !== "pro"
  );

  const handleStackFilter = (stack) => {
    setSelectedStack(selectedStack === stack ? null : stack);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(selectedCategory === category ? null : category);
  };

  return (
    <Wrap>
      <ItemText>
        <h1>{getTranslation(language, "projects.title")}</h1>
      </ItemText>

      <FiltersContainer>
        <FilterButtons>
          <FilterButton
            active={selectedStack === "react"}
            onClick={() => handleStackFilter("react")}
            title="React"
          >
            <FilterIcon src={ReactIcon} alt="React" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "vue"}
            onClick={() => handleStackFilter("vue")}
            title="Vue"
          >
            <FilterIcon src={VueIcon} alt="Vue" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "ruby"}
            onClick={() => handleStackFilter("ruby")}
            title="Ruby"
          >
            <FilterIcon src={RubyIcon} alt="Ruby" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "typescript"}
            onClick={() => handleStackFilter("typescript")}
            title="TypeScript"
          >
            <FilterIcon src={TypescriptIcon} alt="TypeScript" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "javascript"}
            onClick={() => handleStackFilter("javascript")}
            title="JavaScript"
          >
            <FilterIcon src={JavascriptIcon} alt="JavaScript" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "redux"}
            onClick={() => handleStackFilter("redux")}
            title="Redux"
          >
            <FilterIcon src={ReduxIcon} alt="Redux" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "tailwind"}
            onClick={() => handleStackFilter("tailwind")}
            title="Tailwind"
          >
            <FilterIcon src={TailwindIcon} alt="Tailwind" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "firebase"}
            onClick={() => handleStackFilter("firebase")}
            title="Firebase"
          >
            <FilterIcon src={FirebaseIcon} alt="Firebase" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "supabase"}
            onClick={() => handleStackFilter("supabase")}
            title="Supabase"
          >
            <FilterIcon src={SupabaseIcon} alt="Supabase" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "zustand"}
            onClick={() => handleStackFilter("zustand")}
            title="Zustand"
          >
            <FilterIcon src={ZustandIcon} alt="Zustand" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "vuetify"}
            onClick={() => handleStackFilter("vuetify")}
            title="Vuetify"
          >
            <FilterIcon src={VuetifyIcon} alt="Vuetify" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "quasar"}
            onClick={() => handleStackFilter("quasar")}
            title="Quasar"
          >
            <FilterIcon src={QuasarIcon} alt="Quasar" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "pinia"}
            onClick={() => handleStackFilter("pinia")}
            title="Pinia"
          >
            <FilterIcon src={PiniaIcon} alt="Pinia" />
          </FilterButton>
          <FilterButton
            active={selectedStack === "vuex"}
            onClick={() => handleStackFilter("vuex")}
            title="VueX"
          >
            <FilterIcon src={VueXIcon} alt="VueX" />
          </FilterButton>
          <FilterButton
            active={selectedCategory === "frontend"}
            onClick={() => handleCategoryFilter("frontend")}
            title="Frontend"
          >
            <span>
              {getTranslation(language, "projects.filters.frontend") ||
                "Frontend"}
            </span>
          </FilterButton>
          <FilterButton
            active={selectedCategory === "backend"}
            onClick={() => handleCategoryFilter("backend")}
            title="Backend"
          >
            <span>
              {getTranslation(language, "projects.filters.backend") ||
                "Backend"}
            </span>
          </FilterButton>
          <FilterButton
            active={selectedCategory === "fullstack"}
            onClick={() => handleCategoryFilter("fullstack")}
            title="Fullstack"
          >
            <span>
              {getTranslation(language, "projects.filters.fullstack") ||
                "Fullstack"}
            </span>
          </FilterButton>
        </FilterButtons>
        {(selectedStack || selectedCategory) && (
          <ClearFiltersRow>
            <ClearFilters
              onClick={() => {
                setSelectedStack(null);
                setSelectedCategory(null);
              }}
            >
              {getTranslation(language, "projects.filters.clear") || "Clear Filters"}
            </ClearFilters>
          </ClearFiltersRow>
        )}
      </FiltersContainer>

      {professionalProjects.length > 0 && (
        <CardsContainer>
          <SectionHeader>
            <h2>{getTranslation(language, "projects.professional")}</h2>
          </SectionHeader>
          <Cards>
            {professionalProjects.map((project, index) => (
              <ProjectCard
                key={index}
                {...project}
                translationKey={project.translationKey}
              />
            ))}
          </Cards>
        </CardsContainer>
      )}

      {otherProjects.length > 0 && (
        <CardsContainer>
          <SectionHeader>
            <h2>{getTranslation(language, "projects.others")}</h2>
          </SectionHeader>
          <Cards>
            {otherProjects.map((project, index) => (
              <ProjectCard
                key={index}
                {...project}
                translationKey={project.translationKey}
              />
            ))}
          </Cards>
        </CardsContainer>
      )}

      {filteredProjects.length === 0 && (
        <NoResults>
          {getTranslation(language, "projects.noResults") ||
            "No projects found matching the selected filters."}
        </NoResults>
      )}
    </Wrap>
  );
}

export default Projects;

const CardsContainer = styled.div`
  width: 90vw;
  justify-content: flex-start;
`;

const Cards = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  justify-content: center;
  gap: 10px;

  @media (max-width: 1200px) {
    justify-content: center;
    gap: 15px;
  }

  @media (max-width: 768px) {
    justify-content: center;
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 5px;
  }
`;

const Wrap = styled.div`
  background-color: rgba(248, 241, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: 490px) {
    overflow-x: hidden;
  }
`;

const ItemText = styled.div`
  margin-top: 90px;
  color: black;

  h1 {
    @media (max-width: 768px) {
      font-size: 24px;
      margin-top: -4px;
    }
  }
`;

const SectionHeader = styled.div`
  margin: 30px 0px 0px 80px;
  text-align: left;
  position: relative;

  @media (max-width: 768px) {
    margin: 30px 0px 0px 20px;
  }

  @media (max-width: 480px) {
    margin: 20px 0px 0px 10px;
  }

  h2 {
    margin-bottom: 5px;
    font-size: 1.2rem;
    text-transform: uppercase;
    color: black;
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }

  &:after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -5px;
    width: 150px;
    height: 1px;
    background-color: black;
    @media (max-width: 480px) {
      width: 100px;
    }
  }
`;

const FiltersContainer = styled.div`
  width: 90vw;
  max-width: 1200px;
  margin: 30px auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    margin: 20px auto;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  align-items: center;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 2px solid rgba(83, 83, 83, 0.3);
  background-color: ${(props) =>
    props.active ? "rgba(200, 233, 250, 0.5)" : "rgba(255, 255, 255, 0.8)"};
  border-color: ${(props) =>
    props.active ? "rgba(83, 83, 83, 0.6)" : "rgba(83, 83, 83, 0.3)"};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
  color: black;
  min-width: 40px;
  min-height: 40px;

  &:hover {
    background-color: rgba(200, 233, 250, 0.3);
    border-color: rgba(83, 83, 83, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 6px;
    font-size: 0.85rem;
    min-width: 36px;
    min-height: 36px;
  }

  @media (max-width: 480px) {
    padding: 5px;
    font-size: 0.8rem;
    min-width: 32px;
    min-height: 32px;
  }
`;

const FilterIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
  }
`;

const ClearFiltersRow = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ClearFilters = styled.button`
  padding: 8px 16px;
  border: 2px solid rgba(83, 83, 83, 0.3);
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
  color: black;

  &:hover {
    background-color: rgba(200, 233, 250, 0.3);
    border-color: rgba(83, 83, 83, 0.5);
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 0.8rem;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 1.1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 30px 15px;
  }
`;
